import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import counter from "./counter";

const SETTLE = 'opacity,transform,transformOrigin,transition,clipPath';
const QUIET = '.elementor-location-footer';
const OFFSETS = { fade: 24, soft: 12 };
const ICONS = '.elementor-icon, .elementor-image-box-img';
const LIST_ITEMS = '.elementor-icon-list-item';
const LINES = '.elementor-divider-separator';
const LABELS = '.categories-grid__title, .seasons-grid__img-text';
const STEP_PARTS = '.checkout-steps__number, .checkout-steps__line, .checkout-steps__label';
const METHOD_PARTS = '.checkout-shipping-methods li, .checkout-page__payment li, .shipping-method__icon';
const PANEL_PARTS = '.payment-summary__container > :not(.order-summary-details), .order-summary-details > *';
const NEWSLETTER_PARTS = '.green-bg, .newsletter__title, .newsletter-form__top, .newsletter-form__title, .preference-item, .text__input, .form-check, .btn-subscribe';

const TITLES = [
    '.bestsellers__title',
    '.seasons-grid__title',
    '.shop-page__featured-title',
    '.customers-opinions__title',
    '.related.products > h2',
    '.upsells.products > h2',
    '.product_title',
    '.checkout-page__recipient > .section__title',
    '.checkout-page__delivery > .section__title',
    '.checkout-page__payment-section > .section__title',
    '.cart-recommended__title',
].join(', ');

const BLOCKS = [
    '.hero__all-products',
    '.woocommerce-breadcrumb',
    '.shop-page__sidebar-clear',
    '.shop-filter',
    '.shop-page__category-top',
    '.summary.entry-summary > :not(.product_title)',
    '.images__information',
    '.woocommerce-pagination',
    '.customer-info__pros__item',
    '.customers-opinions__google-card',
    '.customers-opinions__add-btn',
    '.customers-opinions__right-header',
    '.customers-opinions__slider',
    '.login-popup--inline',
    '.assistant-cta',
    '.woocommerce-billing-fields__field-wrapper .form-row',
    '#ship-to-different-address',
    '.cart-page__header',
    '.cart-page .customer-info__contact',
].join(', ');

const SKIP = [
    '.elementor-widget-spacer',
    '.elementor-widget-template',
    '.elementor-widget-menu-anchor',
    '.elementor-widget-posts',
    '.elementor-widget-archive-posts',
    '.elementor-widget-loop-grid',
].join(', ');

const OWN_MOTION = '.newsletter-container, .customers-opinions, ul.products, .assistant-cta, .categories-grid, .seasons-grid';

const modes = new WeakMap();

const visibleImage = (el) => Array.from(el.querySelectorAll('img')).find((img) => img.offsetWidth > 0) || null;

const words3d = (text, delay, done) => {
    SplitText.create(text, {
        type: 'lines,words',
        mask: 'lines',
        linesClass: 'ex-line',
        wordsClass: 'ex-word',
        tag: 'span',
        autoSplit: true,
        onSplit: (split) => {
            text.classList.add('ex-split');

            return gsap.from(split.words, {
                yPercent: 140,
                rotationX: -80,
                opacity: 0,
                transformOrigin: '50% 100%',
                transformPerspective: 600,
                duration: 1.15,
                ease: 'expo.out',
                stagger: Math.min(0.05, 0.7 / split.words.length),
                delay,
                onComplete: () => {
                    text.classList.remove('ex-split');
                    split.revert();
                    done();
                },
            });
        },
    });
};

const rise = (distance, options = {}) => ({
    hide(el) {
        gsap.set(el, { opacity: 0, y: distance, scale: options.scale || 1, transition: 'none' });
    },
    show(el, delay) {
        gsap.to(el, {
            opacity: 1,
            y: 0,
            scale: 1,
            delay,
            duration: options.duration || 0.9,
            ease: options.ease || 'power3.out',
            clearProps: SETTLE,
        });
    },
});

const headline = (textOf) => ({
    hide(el) {
        const text = textOf(el);
        const style = window.getComputedStyle(text);
        const size = parseFloat(style.fontSize);
        const content = text.textContent.trim();
        let mode = 'fade';

        if (el.closest(QUIET)) {
            mode = 'soft';
        } else if (style.display === 'block' && size >= 26 && content && !text.querySelector('img, svg, iframe, input, select, textarea')) {
            mode = 'words';
        } else if (size < 26 && content.length <= 40 && !text.children.length && (style.textTransform === 'uppercase' || content === content.toUpperCase())) {
            mode = 'scramble';
        }

        modes.set(el, mode);
        gsap.set(el, OFFSETS[mode] ? { opacity: 0, y: OFFSETS[mode], transition: 'none' } : { opacity: 0, transition: 'none' });
    },
    show(el, delay) {
        const text = textOf(el);
        const mode = modes.get(el);

        if (mode === 'words') {
            gsap.set(el, { opacity: 1 });
            words3d(text, delay, () => gsap.set(el, { clearProps: SETTLE }));
            return;
        }

        if (mode === 'scramble') {
            const original = text.textContent;

            text.setAttribute('aria-label', original.trim());
            gsap.to(el, { opacity: 1, duration: 0.3, delay });
            gsap.to(text, {
                duration: Math.min(1.4, 0.5 + original.length * 0.03),
                delay,
                ease: 'none',
                scrambleText: { text: original, chars: 'upperCase', revealDelay: 0.3, speed: 0.6 },
                onComplete: () => {
                    text.textContent = original;
                    text.removeAttribute('aria-label');
                    gsap.set(el, { clearProps: SETTLE });
                },
            });
            return;
        }

        if (mode === 'soft') {
            gsap.to(el, { opacity: 1, y: 0, duration: 1.2, ease: 'power2.out', delay, clearProps: SETTLE });
            return;
        }

        gsap.to(el, { opacity: 1, y: 0, delay, clearProps: SETTLE });
    },
});

const paragraphs = {
    hide(el) {
        const lines = el.textContent.length < 1600
            && !el.querySelector('img, iframe, video, table, form, input')
            && el.querySelectorAll('p, li').length > 0;

        modes.set(el, lines);
        gsap.set(el, lines ? { opacity: 0, transition: 'none' } : { opacity: 0, y: 24, transition: 'none' });
    },
    show(el, delay) {
        if (!modes.get(el)) {
            gsap.to(el, { opacity: 1, y: 0, delay, clearProps: SETTLE });
            return;
        }

        gsap.set(el, { opacity: 1 });
        SplitText.create(el.querySelectorAll('p, li'), {
            type: 'lines',
            linesClass: 'ex-line',
            tag: 'span',
            autoSplit: true,
            onSplit: (split) => gsap.from(split.lines, {
                opacity: 0,
                y: 22,
                duration: 0.9,
                ease: 'power3.out',
                stagger: Math.min(0.08, 0.9 / split.lines.length),
                delay,
                onComplete: () => {
                    split.revert();
                    gsap.set(el, { clearProps: SETTLE });
                },
            }),
        });
    },
};

const media = {
    hide(el) {
        const img = visibleImage(el);

        modes.set(el, img);
        gsap.set(el, { opacity: 0, transition: 'none' });

        if (img) {
            gsap.set(img, { scale: 1.3, transformOrigin: '50% 50%', transition: 'none' });
        }
    },
    show(el, delay) {
        const img = modes.get(el);

        gsap.fromTo(el, { opacity: 1, clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.25, ease: 'expo.inOut', delay, clearProps: SETTLE });

        if (img) {
            gsap.to(img, { scale: 1, duration: 1.7, ease: 'expo.out', delay: delay + 0.15, clearProps: SETTLE });
        }
    },
};

const card = (imageOf) => ({
    hide(el) {
        const img = imageOf(el);

        gsap.set(el, { opacity: 0, y: 70, rotationX: -32, transformOrigin: '50% 0%', transformPerspective: 1100, transition: 'none' });

        if (img) {
            gsap.set(img, { scale: 1.2, clipPath: 'inset(8.33% round 6.67px)', transition: 'none' });
        }
    },
    show(el, delay) {
        const img = imageOf(el);

        gsap.to(el, { opacity: 1, y: 0, rotationX: 0, duration: 1.15, ease: 'power4.out', delay, clearProps: SETTLE });

        if (img) {
            gsap.to(img, { scale: 1, clipPath: 'inset(0% round 8px)', duration: 1.5, ease: 'expo.out', delay: delay + 0.05, clearProps: SETTLE });
        }
    },
});

const wipe = {
    hide(el) {
        gsap.set(el, { opacity: 0, transition: 'none' });
    },
    show(el, delay) {
        const input = el.querySelector('input[type="search"], .search-field');

        gsap.fromTo(el, { opacity: 1, clipPath: 'inset(0% 100% 0% 0% round 999px)' }, {
            clipPath: 'inset(0% 0% 0% 0% round 999px)',
            duration: 1.1,
            ease: 'expo.inOut',
            delay,
            clearProps: SETTLE,
        });

        if (input) {
            gsap.fromTo(input, { textIndent: 60 }, { textIndent: 0, duration: 1, delay: delay + 0.45, ease: 'power3.out', clearProps: 'textIndent' });
        }
    },
};

const pop = {
    hide(el) {
        gsap.set(el, { opacity: 0, y: 24, scale: 0.85, transition: 'none' });
    },
    show(el, delay) {
        gsap.to(el, { opacity: 1, y: 0, scale: 1, duration: 0.9, ease: 'back.out(1.8)', delay, clearProps: SETTLE });
    },
};

const iconBox = {
    hide(el) {
        gsap.set(el, { opacity: 0, y: 40, transition: 'none' });
        gsap.set(el.querySelectorAll(ICONS), { scale: 0.3, rotation: -120, transition: 'none' });
    },
    show(el, delay) {
        gsap.to(el, { opacity: 1, y: 0, duration: 1, delay, clearProps: SETTLE });
        gsap.to(el.querySelectorAll(ICONS), {
            scale: 1,
            rotation: 0,
            delay: delay + 0.2,
            duration: 1,
            ease: 'back.out(2.2)',
            clearProps: SETTLE,
        });
        el.querySelectorAll('.elementor-icon-box-title, .elementor-image-box-title').forEach((title) => counter.run(title, delay + 0.1));
    },
};

const iconList = {
    hide(el) {
        gsap.set(el, { opacity: 0, transition: 'none' });
        gsap.set(el.querySelectorAll(LIST_ITEMS), { opacity: 0, x: -24, transition: 'none' });
    },
    show(el, delay) {
        gsap.to(el, { opacity: 1, duration: 0.4, delay, clearProps: SETTLE });
        gsap.to(el.querySelectorAll(LIST_ITEMS), {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'back.out(1.6)',
            stagger: 0.08,
            delay: delay + 0.05,
            clearProps: SETTLE,
        });
    },
};

const divider = {
    hide(el) {
        const line = el.querySelector(LINES);

        gsap.set(el, { opacity: 0, transition: 'none' });

        if (line && !el.querySelector('.elementor-divider__element')) {
            const align = window.getComputedStyle(el.querySelector('.elementor-divider') || el).textAlign;
            let origin = '0% 50%';

            if (align === 'center') {
                origin = '50% 50%';
            } else if (align === 'right' || align === 'end') {
                origin = '100% 50%';
            }

            gsap.set(line, { scaleX: 0, transformOrigin: origin, transition: 'none' });
        }
    },
    show(el, delay) {
        gsap.to(el, { opacity: 1, duration: 0.3, delay, clearProps: SETTLE });
        gsap.to(el.querySelectorAll(LINES), { scaleX: 1, duration: 1.3, ease: 'expo.inOut', delay, clearProps: SETTLE });
    },
};

const tile = {
    hide(el) {
        const img = el.querySelector('img');

        gsap.set(el, { opacity: 0, '--ex-zoom': 1.35, transition: 'none' });
        gsap.set(el.querySelectorAll(LABELS), { opacity: 0, y: 40, transition: 'none' });

        if (img) {
            gsap.set(img, { scale: 1.25, transition: 'none' });
        }
    },
    show(el, delay) {
        const img = el.querySelector('img');

        gsap.fromTo(el, { opacity: 1, clipPath: 'inset(100% -10% -10% -10%)' }, {
            clipPath: 'inset(0% -10% -10% -10%)',
            '--ex-zoom': 1,
            duration: 1.3,
            ease: 'expo.inOut',
            delay,
            clearProps: SETTLE + ',--ex-zoom',
        });
        gsap.to(el.querySelectorAll(LABELS), { opacity: 1, y: 0, delay: delay + 0.55, duration: 0.9, ease: 'back.out(1.6)', clearProps: SETTLE });

        if (img) {
            gsap.to(img, { scale: 1, duration: 1.7, ease: 'expo.out', delay: delay + 0.1, clearProps: SETTLE });
        }
    },
};

const newsletter = {
    hide(el) {
        const part = (selector) => el.querySelectorAll(selector);

        gsap.set(el, { opacity: 0, y: 60, transition: 'none' });
        gsap.set(part('.green-bg'), { clipPath: 'inset(0% 0% 0% 100%)' });
        gsap.set(part('.newsletter__title, .newsletter-form__top, .newsletter-form__title'), { opacity: 0, y: 20 });
        gsap.set(part('.preference-item'), { opacity: 0, x: -70, rotation: -5, transition: 'none' });
        gsap.set(part('.text__input, .form-check'), { opacity: 0, y: 26 });
        gsap.set(part('.btn-subscribe'), { opacity: 0, scale: 0.6, transition: 'none' });
    },
    show(el, delay) {
        const part = (selector) => el.querySelectorAll(selector);
        const title = el.querySelector('.newsletter-form__title');

        gsap.timeline({ delay, onComplete: () => gsap.set([el].concat(Array.from(part(NEWSLETTER_PARTS))), { clearProps: SETTLE }) })
            .to(el, { opacity: 1, y: 0, duration: 1, ease: 'power3.out' })
            .to(part('.green-bg'), { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.2, ease: 'expo.inOut' }, 0.15)
            .to(part('.newsletter__title'), { opacity: 1, y: 0, duration: 0.6 }, 0.4)
            .to(part('.preference-item'), { opacity: 1, x: 0, rotation: 0, duration: 1, ease: 'back.out(1.5)', stagger: 0.14 }, 0.45)
            .to(part('.newsletter-form__top'), { opacity: 1, y: 0, duration: 0.6 }, 0.65)
            .call(() => {
                if (title && title.offsetWidth) {
                    gsap.set(title, { opacity: 1, y: 0 });
                    words3d(title, 0, () => {});
                } else if (title) {
                    gsap.set(title, { opacity: 1, y: 0 });
                }
            }, null, 0.7)
            .to(part('.text__input, .form-check'), { opacity: 1, y: 0, duration: 0.8, stagger: 0.12 }, 0.95)
            .to(part('.btn-subscribe'), { opacity: 1, scale: 1, duration: 0.9, ease: 'elastic.out(1, 0.5)' }, 1.25);
    },
};

const slide = (distance) => ({
    hide(el) {
        gsap.set(el, { opacity: 0, x: distance, transition: 'none' });
    },
    show(el, delay) {
        gsap.to(el, { opacity: 1, x: 0, duration: 0.9, ease: 'back.out(1.3)', delay, clearProps: SETTLE });
    },
});

const steps = {
    hide(el) {
        gsap.set(el, { opacity: 0, transition: 'none' });
        gsap.set(el.querySelectorAll('.checkout-steps__number'), { scale: 0, rotation: -120, transition: 'none' });
        gsap.set(el.querySelectorAll('.checkout-steps__line'), { scaleX: 0, transformOrigin: '0% 50%', transition: 'none' });
        gsap.set(el.querySelectorAll('.checkout-steps__label'), { opacity: 0, y: 12, transition: 'none' });
    },
    show(el, delay) {
        const lines = el.querySelectorAll('.checkout-steps__line');
        const active = el.querySelector('.checkout-steps__item--active .checkout-steps__number');
        const timeline = gsap.timeline({
            delay,
            onComplete: () => gsap.set([el].concat(Array.from(el.querySelectorAll(STEP_PARTS))), { clearProps: SETTLE + ',boxShadow' }),
        });

        timeline.set(el, { opacity: 1 });
        el.querySelectorAll('.checkout-steps__item').forEach((item, index) => {
            const at = index * 0.5;

            timeline
                .to(item.querySelector('.checkout-steps__number'), { scale: 1, rotation: 0, duration: 0.75, ease: 'back.out(2.2)' }, at)
                .to(item.querySelector('.checkout-steps__label'), { opacity: 1, y: 0, duration: 0.6 }, at + 0.15);

            if (lines[index]) {
                timeline.to(lines[index], { scaleX: 1, duration: 0.6, ease: 'power2.inOut' }, at + 0.25);
            }
        });

        if (active) {
            timeline.fromTo(active, { boxShadow: '0 0 0 0 rgba(9, 104, 172, 0.45)' }, {
                boxShadow: '0 0 0 18px rgba(9, 104, 172, 0)',
                duration: 1.2,
                ease: 'power2.out',
                repeat: 1,
            }, 0.9);
        }
    },
};

const methods = {
    hide(el) {
        gsap.set(el, { opacity: 0, transition: 'none' });
        gsap.set(el.querySelectorAll('li'), { opacity: 0, x: -48, transition: 'none' });
        gsap.set(el.querySelectorAll('.shipping-method__icon'), { scale: 0.3, rotation: -25, transition: 'none' });
    },
    show(el, delay) {
        gsap.to(el, { opacity: 1, duration: 0.35, delay, clearProps: SETTLE });
        gsap.to(el.querySelectorAll('li'), { opacity: 1, x: 0, duration: 0.8, ease: 'back.out(1.5)', stagger: 0.1, delay: delay + 0.05, clearProps: SETTLE });
        gsap.to(el.querySelectorAll('.shipping-method__icon'), { scale: 1, rotation: 0, duration: 0.9, ease: 'back.out(2.4)', stagger: 0.1, delay: delay + 0.2, clearProps: SETTLE });
    },
};

const panel = {
    hide(el) {
        gsap.set(el, { opacity: 0, x: 90, rotationY: -14, transformPerspective: 1400, transformOrigin: '100% 50%', transition: 'none' });
        gsap.set(el.querySelectorAll(PANEL_PARTS), { opacity: 0, y: 22, transition: 'none' });
    },
    show(el, delay) {
        const start = Math.min(delay, 0.3);

        gsap.to(el, { opacity: 1, x: 0, rotationY: 0, duration: 1.2, ease: 'expo.out', delay: start, clearProps: SETTLE });
        gsap.to(el.querySelectorAll(PANEL_PARTS), { opacity: 1, y: 0, duration: 0.7, stagger: 0.06, delay: start + 0.3, clearProps: SETTLE });
    },
};

const define = (selector, prehide, behaviour, skip = null) => Object.assign({ selector, prehide, skip }, behaviour);

const animators = {
    parts: [ICONS, LIST_ITEMS, LINES, LABELS, NEWSLETTER_PARTS, STEP_PARTS, METHOD_PARTS, PANEL_PARTS, 'img'].join(', '),
    list: [
        define('.elementor-widget-heading', true, headline((el) => el.querySelector('.elementor-heading-title') || el)),
        define('.elementor-widget-text-editor', true, paragraphs),
        define('.elementor-widget-button', true, pop),
        define('.elementor-widget-icon-box, .elementor-widget-image-box', true, iconBox),
        define('.elementor-widget-icon-list', true, iconList),
        define('.elementor-widget-divider', true, divider),
        define('.elementor-post', true, card(() => null)),
        define('.elementor-widget-image', false, media),
        define('.elementor-widget', false, rise(40), (el) => el.matches(SKIP) || !!el.querySelector(OWN_MOTION)),
        define('.newsletter-container', true, newsletter),
        define(TITLES, true, headline((el) => el)),
        define('.hero__category, .shop-page__featured-item', true, pop),
        define('ul.products > li.product', true, card((el) => el.querySelector('.ex-card-media > img:not(.ex-card-alt)'))),
        define('.categories-grid__item, .seasons-grid__img', true, tile),
        define('.hero__img, .shop-page__banner, .woocommerce-product-gallery', true, media),
        define('.customers-opinions__photo', false, media),
        define('.hero__search', true, wipe),
        define('.checkout-steps__container', true, steps),
        define('.checkout-shipping-methods, .checkout-page__payment', true, methods),
        define('.payment-summary__container', true, panel),
        define('.cart-page .cart-product', true, slide(-50)),
        define(BLOCKS, true, rise(30)),
    ],
};

export default animators;
